import { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Select, Input } from '@/components/ui'
import { useBienContext } from '../BienContext'
import { 
  createEntretien, 
  emptyEntretien, 
  updateEntretien, 
  startNewPrestataireCreation,
  finishNewPrestataireCreation,
  getEntretienTypeOptions, 
  FactureStatuses, 
  cancelNewPrestataireCreation
} from '../../entretiens/entretiensSlice'
import { facturationEntiteOptions } from '../../interventions/interventionsSlice'

import SuggestionField from "../../prestataires/SuggestionField"
import PrestataireForm from '../../prestataires/Form'

import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'

export default ({ entretien_type_id = null, onClose = () => {}, onSuccess = () => {}, useLocalState = false }) => {
  const dispatch = useDispatch()
  const { bien } = useBienContext()
  
  // Récupérer l'entretien depuis le state Redux
  const reduxEntretien = useSelector(state => state.entretiens.inEdition || emptyEntretien)
  
  // Utiliser un state local pour l'entretien si demandé (pour les tâches de renouvellement)
  const [localEntretien, setLocalEntretien] = useState({
    ...emptyEntretien,
    entretien_type_id: entretien_type_id || emptyEntretien.entretien_type_id
  })
  
  // Utiliser soit l'entretien local, soit l'entretien Redux selon le contexte
  const entretien = useLocalState ? localEntretien : reduxEntretien
  
  // Récupérer les erreurs depuis le state Redux
  const errors = useSelector((state) => state.entretiens.requestStatus.errors)
  const isCreating = useSelector((state) => state.entretiens.loadingStatus.create)
  const isUpdating = useSelector((state) => state.entretiens.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entiteFacturableOptions, setEntiteFacturableOptions] = useState([])

  const isCreatingNewPrestataire = useSelector( state => state.entretiens.isCreatingNewPrestataire)

  // Initialiser le formulaire avec l'entretien approprié
  const { handleSubmit, reset, watch,
    setValue, getValues,
    control, formState: { errors: formErrors }
  } = useForm({
    defaultValues: entretien,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onReset = () => {
    reset(entretien)
  }

  // useFormConfirmation feature
  // ajoute les states, le useEffect pour surveiller l'état du formulaire
  // et s'assurer s'il a été modifié ou pas pour afficher une boîte de 
  // confirmation lorsque le bouton "Annuler" est cliqué.
  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose
  })

  // Options de facturation basées sur le bien (si disponible)
  useEffect(() => {
    if(bien) {
      setEntiteFacturableOptions(facturationEntiteOptions(bien))
    }
  }, [bien])

  // Initialiser le formulaire avec les données de l'entretien
  useEffect(() => {
    reset(entretien)

    Object.keys(entretien).forEach(key => {
      if (key in entretien) {
        setValue(key, entretien[key])
      }
    })

    // useFormConfirmation feature
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)
  }, [entretien])
  
  // Si on utilise le state local et qu'on a un entretien_type_id, initialiser l'entretien local
  useEffect(() => {
    if (useLocalState && entretien_type_id) {
      setLocalEntretien(prev => ({
        ...prev,
        entretien_type_id: entretien_type_id
      }))
    }
  }, [useLocalState, entretien_type_id])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, onSuccess, dispatch])

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if(Array.isArray(errorsMsg)) {
      return errorsMsg.join(", ")
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le bien existe déjà
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      if (entretien.id) {
        await dispatch(updateEntretien({
          bien_id: bien.id,
          id: entretien.id,
          data: formData
        }))
      } else {
        await dispatch(createEntretien({data: formData, bien_id: bien.id}))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const handleNewPrestataire = () => {
    dispatch(startNewPrestataireCreation())
  }

  const handlePrestataireCreated = (newPrestataire) => {
    if (useLocalState) {
      // Mettre à jour l'entretien local avec le nouveau prestataire
      setLocalEntretien({
        ...localEntretien,
        prestataire: newPrestataire
      })
    } else {
      // Utiliser l'action Redux pour mettre à jour l'entretien global
      dispatch(finishNewPrestataireCreation(newPrestataire))
    }
    dispatch(cancelNewPrestataireCreation())
  }

  const entretienTypeOptions = getEntretienTypeOptions(bien)

  const isInEntretiensReguliers = () => entretienTypeOptions.map( i => i.value )
    .includes(entretien.entretien_type_id)
  
  return(
    <div className="min-h-[475px]">
      { isCreatingNewPrestataire ? 
        <PrestataireForm 
          onClose={() => {
            dispatch(cancelNewPrestataireCreation())
          }}
          onPrestataireCreated={handlePrestataireCreated} 
        /> : 
        <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              {entretien.id ? 'Modifier' : 'Ajouter'} un entretien
            </h2>
          </div>

          <div className="card bg-base-100 border border-base-200 rounded-none mb-4">
            <div className="grid grid-cols-3 gap-2 p-4">

              <div className='flex flex-col pl-2 space-y-4'>
                { (!entretien.entretien_type_id || isInEntretiensReguliers()) ?
                  <Select.Form name='entretien_type_id'
                    label="Type d'entretien"
                    control={control}
                    options={entretienTypeOptions}
                    validator={{hasError, getErrorMessage}}
                    className='w-full'
                  /> :
                  <Input.Display label="Type d'entretien" value={entretien.entretien_type?.nom || ''} />
                }
              </div>
              
              <div className='flex flex-col pl-2 space-y-4'>
                <div className='w-full'>
                  <Controller 
                    name='prestataire'
                    control={control}
                    className=''
                    render={({field}) => (
                      <SuggestionField
                        prestataireField={field}
                        originalData={entretien.prestataire}
                        label='Prestataire'
                        onNewPrestataire={handleNewPrestataire}
                        validator={{hasError, getErrorMessage}}
                      />
                    )}
                  />
                </div>

                <Input.Date name="date_prestation"
                  label="Date de la prestation"
                  control={control}
                  className="w-full"
                />
              </div>

              <div className='flex flex-col pl-2 space-y-4'>
                <Input.Currency name='montant'
                  label="Montant"
                  control={control}
                />
                <Select.Form
                  name='entite_facturable'
                  control={control}
                  label='Entité à facturer'
                  options={entiteFacturableOptions}
                  validator={{hasError, getErrorMessage}}
                  className='w-full'
                />
                <Select.Form name='statut_facture'
                  label="Statut de la facture"
                  control={control}
                  options={FactureStatuses}
                  validator={{hasError, getErrorMessage}}
                  className='w-full'
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleFormCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2`}
              disabled={isLoading}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
        <FormConfirmDialog />
        </>
      }
    </div>
  )


}